import buildWorkflowSnapshotDiffs from '@/lib/buildWorkflowSnapshotDiffs.js';
import computeColor from '@/lib/computeColor';
import findStepApp from '@/lib/findStepApp.js';
import findStepAppMetadata from '@/lib/findStepAppMetadata.js';
import { createSelector } from '@/redux/lib/createSelector.js';
import { query } from '@/redux/selectors/querySelectors.js';
import selectors from '@/redux/selectors/selectors';
import { prefs } from '@/redux/selectors/uiSelectors.js';
import { companyId } from '@/redux/selectors/userSelectors';
import { tutorialScripts, workflowId, workflowLogs, workflows, workflowsMetadata, workflowSnapshots, workflowVersion } from '@/redux/selectors/workflowSelectors';
import { flattenGraphSequence } from '@shared/graph/flattenGraphSequence.js';
import { UTILITY_SLUGS } from '@shared/schema/constants.js';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { formatNestedAttributeKey } from '@shared/workflow/formatNestedAttributeKey.js';
import { getWorkflowStepDependencies } from '@shared/workflow/getWorkflowStepDependencies.js';
import getTransformAttributes from '../../components/workflowInputVariablePane/lib/getTransformAttributes.js';
import getParentIterationStepIds from '../../lib/getParentIterationStepIds.js';
import { WHEN_CALLED_SELECTION } from '../../lib/whenCalledSelection.js';

const stableArray = [];

const SPECIAL_APP_SLUGS = [...UTILITY_SLUGS, 'appSelector', 'triggerSelector'];
const EMPTY_OBJECT = Object.freeze({});

export const workflowById = createSelector(
  'env-workflowById',
  workflows,
  (_, workflowId) => workflowId,
  (_, __, workflowVersion) => workflowVersion,
  (workflow, workflowId, workflowVersion = 'draft') => workflow.find((n) => n.id === workflowId && n.version === workflowVersion)
);

export const activeWorkflow = createSelector(
  'activeWorkflow',
  [workflowId, workflowVersion, workflows, workflowsMetadata], //
  (workflowId, workflowVersion, workflows, workflowsMetadata) => {
    const versionToUse = workflowVersion || 'draft';
    const metadata = workflowsMetadata?.find((n) => n?.id === workflowId);
    const loadedPublishedVersion = workflows?.find((workflow) => workflow?.id === workflowId && workflow?.publishedVersion === workflow?.version)?.publishedVersion;
    const publishedVersion = metadata?.publishedVersion || loadedPublishedVersion || 'draft';
    const workflow = versionToUse === 'published' ? workflows?.find((w) => w.id === workflowId && publishedVersion === w.version) : workflows?.find((w) => w.id === workflowId && w.version === versionToUse);

    if (workflow && metadata) {
      workflow.meta = metadata;
    }
    return workflow;
  }
);

export const activeWorkflowMetadata = createSelector('activeWorkflowMetadata', activeWorkflow, workflowsMetadata, (workflow, workflowsMetadata) => {
  return workflowsMetadata?.find((n) => n?.id === workflow?.id);
});

export const workflowCanvasStepDepth = createSelector('workflowCanvasStepDepth', [activeWorkflow], (workflow) => {
  const graph = workflow?.graph;
  const steps = workflow?.steps;
  if (!graph || !steps?.length) {
    return 1;
  }
  const sequence = flattenGraphSequence(graph, steps, null, true);
  const maxLevel = sequence.reduce((max, n) => Math.max(max, n?.level ?? 0), 0);
  return maxLevel + 1;
});

// The environment the editor is operating in. This MUST resolve the same way the run
// dispatchers do (`workflowRunEnvironment-<workflowId>` pref, else the company default),
// otherwise editor surfaces show data from an environment the workflow will not run in.
export const activeWorkflowEnvironment = createSelector('activeWorkflowEnvironment', activeWorkflow, selectors.environments, prefs, (workflow, environments, prefs) => {
  const runEnvironment = prefs?.[`workflowRunEnvironment-${workflow?.id}`];
  return environments?.find((n) => n?.id === runEnvironment) || environments?.find((env) => env?.isDefault);
});

// Reads, writes and deletes on the memory panel must all target the environment the displayed
// entries were actually enumerated for, never the workflow's configured `meta.environmentId`.
export const activeWorkflowMemoryEnvironmentId = createSelector(
  'activeWorkflowMemoryEnvironmentId',
  [selectors.activeWorkflowLoadedMemoryScope, activeWorkflowEnvironment], //
  // Only a resolved enumeration can speak for what is on screen. While it is unresolved the
  // active environment is the right target, because that is the scope about to be loaded.
  (loadedScope, environment) => (loadedScope?.isLoaded ? loadedScope?.environmentId : null) || environment?.id
);

export const activeWorkflowSimpleSteps = createSelector('activeWorkflowSimpleSteps', activeWorkflow, (activeWorkflow) => {
  return activeWorkflow?.steps?.map(({ arguments: _, options: __, meta: { branches, ...___ }, updatedOn, ...rest }) => ({ meta: { branches }, ...rest }));
});

export const activeWorkflowApps = createSelector('activeWorkflowApps', activeWorkflowSimpleSteps, selectors.appsMetadata, selectors.companyCredentials, (workflowSteps, appsMetadata, credentials) => {
  if (!workflowSteps) return stableArray;

  const creds = credentials?.sort(sortByProp(['valid', 'updatedOn'], ['desc', 'desc']));

  const appIds = [
    ...new Set(
      workflowSteps
        .sort((a, b) => a.stepNumber - b.stepNumber)
        .filter((step) => step.type !== 'trigger')
        .map((step) => step?.appId)
    )
  ].filter(Boolean);

  return appIds
    .map((appId) => appsMetadata?.find((app) => app.id === appId))
    .filter((app) => !SPECIAL_APP_SLUGS.includes(app?.slug))
    .map((app) => {
      const steps = workflowSteps.filter((step) => step?.appId === app?.id).sort((a, b) => a.stepNumber - b.stepNumber);

      return {
        ...app,
        credentials: creds?.filter((c) => c.appId === app?.id),
        appSteps: steps.map((step) => ({
          ...step,
          selected: false,
          credential: creds?.find((c) => c.credentialId === step?.credentialId)
        }))
      };
    });
});
// Step lookups by id run for every canvas card on every store change. Each card asks for its own
// id, so a reselect selector taking the id as an argument (cache size one) would recompute a
// linear scan of the steps for every card; the id index below is built once per workflow change.
const EMPTY_STEPS_BY_ID = new Map();
export const activeWorkflowStepsById = createSelector('activeWorkflowStepsById', activeWorkflow, (workflow) => {
  const steps = workflow?.steps;
  if (!Array.isArray(steps)) {
    return EMPTY_STEPS_BY_ID;
  }
  // First match wins, like the `.find()` calls this replaces. Every step write path merges by id
  // (`mergeByProp('id', ...)` in the workflow reducer), so duplicates should not reach here; the
  // explicit guard keeps the index faithful to the scan rather than relying on that invariant.
  const stepsById = new Map();
  for (const step of steps) {
    if (!stepsById.has(step?.id)) {
      stepsById.set(step?.id, step);
    }
  }
  return stepsById;
});
export const activeWorkflowStep = createSelector('activeWorkflowStep', [(state) => query(state, 'stepId'), (state) => state.workflow?.selectedSteps, activeWorkflowStepsById], (stepId, selectedSteps, stepsById) => {
  const queryStep = stepsById.get(stepId);
  const selectedStep = selectedSteps?.length === 1 ? stepsById.get(selectedSteps[0]) : null;

  return queryStep || selectedStep;
});
export const activeWorkflowArguments = createSelector('activeWorkflowArguments', activeWorkflow, (activeWorkflow) => activeWorkflow?.meta?.arguments);
export const activeWorkflowArgumentInstances = createSelector('activeWorkflowArgumentInstances', activeWorkflow, (activeWorkflow) => activeWorkflow?.meta?.argumentInstances);
export const activeWorkflowArgumentEnvironments = createSelector('activeWorkflowArgumentEnvironments', activeWorkflow, (activeWorkflow) => activeWorkflow?.meta?.argumentEnvironments);
export const activeWorkflowStepDependencies = createSelector('activeWorkflowStepDependencies', activeWorkflowStep, (step) => getWorkflowStepDependencies(step));
// Dependants are indexed once per workflow change for the same reason as the step index above:
// every card's unused/dependent indicators ask for their own step id on every store change.
export const activeWorkflowDependantStepsByStepId = createSelector('activeWorkflowDependantStepsByStepId', activeWorkflow, (workflow) => {
  const dependantsByStepId = new Map();
  for (const step of workflow?.steps || stableArray) {
    for (const dependencyStepId of getWorkflowStepDependencies(step) || stableArray) {
      const dependants = dependantsByStepId.get(dependencyStepId);
      if (!dependants) {
        dependantsByStepId.set(dependencyStepId, [step]);
      } else if (!dependants.includes(step)) {
        dependants.push(step);
      }
    }
  }
  return dependantsByStepId;
});
export const activeWorkflowStepDependantStepsByStepId = (state, stepId) => activeWorkflowDependantStepsByStepId(state).get(stepId) || stableArray;
export const activeWorkflowStepApp = createSelector('activeWorkflowStepApp', activeWorkflowStep, selectors.appsVersions, (step, apps) => findStepApp(step, apps));
export const activeWorkflowStepAppTriggerActions = createSelector('activeWorkflowStepAppTriggerActions', activeWorkflowStepApp, (app) => app?.actions?.filter((a) => a?.slug === 'deleteTrigger' || a?.slug === 'updateTrigger'));
export const activeWorkflowStepIsTrigger = createSelector('activeWorkflowStepIsTrigger', activeWorkflowStepApp, activeWorkflowStep, (app, step) => app?.enableTriggers && app?.triggerMode === 'onDemandWebhook' && step?.type === 'trigger');
export const workflowTriggerData = (state) => state.workflow.workflowStepTriggerData;
export const activeWorkflowStepAppTriggerData = createSelector(
  'activeWorkflowStepAppTriggerData',
  [(state) => query(state, 'stepId'), workflowTriggerData], //
  (stepId, workflowTriggerData) => workflowTriggerData?.[stepId]
);
export const activeWorkflowEnvironmentConnections = createSelector('activeWorkflowEnvironmentConnections', activeWorkflow, (activeWorkflow) => activeWorkflow?.meta?.appEnvironmentConnections);
export const activeWorkflowAppEnvironmentConnections = createSelector('activeWorkflowAppEnvironmentConnections', activeWorkflow, activeWorkflowStepApp, (activeWorkflow, app) => activeWorkflow?.meta?.appEnvironmentConnections?.[app?.id] || {});
export const activeWorkflowStepAppMetadata = createSelector('activeWorkflowStepAppMetadata', activeWorkflowStep, selectors.appsMetadata, (step, appsMetadata) => findStepAppMetadata(step, appsMetadata));
export const activeWorkflowStepAppAction = createSelector('activeWorkflowStepAppAction', activeWorkflowStepApp, activeWorkflowStep, (app, step) => app?.actions?.find((a) => a.id === step?.actionId));
export const activeWorkflowStepAppActionArgumentDefinitions = createSelector('activeWorkflowStepAppActionArgumentDefinitions', activeWorkflowStepAppAction, (action) => action?.arguments);
export const activeWorkflowStepAppAPI = createSelector('activeWorkflowStepAppAPI', activeWorkflowStepAppAction, activeWorkflowStepApp, (action, app) => {
  if (action?.api?.id) {
    return app?.api?.find((a) => a?.id === action?.api?.id);
  }
  return app?.api?.find((a) => a?.type === action?.api?.type);
});
export const activeWorkflowStepAppAPIArgumentDefinitions = createSelector('activeWorkflowStepAppAPIArgumentDefinitions', activeWorkflowStepAppAPI, (api) => api?.arguments);
export const activeWorkflowStepArguments = createSelector('activeWorkflowStepArguments', activeWorkflowStep, (step) => (step?.appSlug === 'functionInputs' ? step?.options?.testArguments : step?.arguments));
export const activeWorkflowStepArgumentDefinitions = createSelector('activeWorkflowStepArgumentDefinitions', activeWorkflowStepAppAPIArgumentDefinitions, activeWorkflowStepAppActionArgumentDefinitions, (apiDefinitions, actionDefinitions) => [...(apiDefinitions || []), ...(actionDefinitions || [])]);
export const activeWorkflowStepOptions = createSelector('activeWorkflowStepOptions', activeWorkflowStep, (step) => step?.options);
export const activeWorkflowStepCredentials = createSelector('activeWorkflowStepCredentials', activeWorkflowStep, selectors.credentials, (step, credentials) => credentials?.find((n) => n?.credentialId === step?.credentialId));
export const activeWorkflowStepAuth = createSelector('activeWorkflowStepAuth', activeWorkflowStepApp, activeWorkflowStepCredentials, (app, credentials) => app?.authentication?.find((n) => n?.type === credentials?.meta?.type) || app?.authentication?.[0]); // todo do default
export const activeWorkflowStepActiveCredentials = createSelector(
  'activeWorkflowStepActiveCredentials',
  selectors.credentials,
  (_, credentialId) => credentialId,
  (credentials, credentialId) => credentials?.find((n) => n?.credentialId === credentialId)
);
export const activeWorkflowStepActiveAuth = createSelector('activeWorkflowStepActiveAuth', activeWorkflowStepApp, activeWorkflowStepActiveCredentials, (app, credentials) => app?.authentication?.find((n) => n?.type === credentials?.meta?.type || n?.type === credentials?.secrets?.type));
export const activeWorkflowGraphStepType = createSelector(
  'activeWorkflowGraphStepType',
  [(state) => query(state, 'stepId'), activeWorkflow], //
  (stepId, activeWorkflow) => activeWorkflow?.graph[stepId]?.type
);
export const activeWorkflowStepArgument = createSelector(
  'activeWorkflowStepArgument',
  activeWorkflowStepArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);
export const activeWorkflowStepOptionsArgument = createSelector(
  'activeWorkflowStepOptionsArgument',
  activeWorkflowStepOptions,
  (_, key, __) => key,
  (_, __, index) => index,
  (options, key, index) => (index !== undefined ? options?.arguments?.[key]?.[index] : options?.arguments?.[key])
);

export const activeWorkflowStepById = (state, stepId) => activeWorkflowStepsById(state).get(stepId);

// Per-step app lookups are indexed for the same reason as the step index: every card resolves its
// app (and its metadata, a catalog of thousands of entries) on every store change. The indexes
// keep the first match, exactly like the linear scans they replace.
export const activeWorkflowStepAppsByStepId = createSelector('activeWorkflowStepAppsByStepId', [activeWorkflowStepsById, selectors.appsVersions], (stepsById, apps) => {
  const appsByStepId = new Map();
  for (const [stepId, step] of stepsById) {
    appsByStepId.set(stepId, findStepApp(step, apps));
  }
  return appsByStepId;
});
export const activeWorkflowStepAppMetadataByStepId = createSelector('activeWorkflowStepAppMetadataByStepId', [activeWorkflowStepsById, selectors.appsMetadata], (stepsById, appsMetadata) => {
  const metadataByAppId = new Map();
  for (const appMetadata of appsMetadata || stableArray) {
    if (!metadataByAppId.has(appMetadata?.id)) {
      metadataByAppId.set(appMetadata?.id, appMetadata);
    }
  }
  const metadataByStepId = new Map();
  for (const [stepId, step] of stepsById) {
    metadataByStepId.set(stepId, metadataByAppId.get(step?.appId));
  }
  return metadataByStepId;
});
export const activeWorkflowStepByIdApp = (state, stepId) => activeWorkflowStepAppsByStepId(state).get(stepId);
export const activeWorkflowStepByIdAppMetadata = (state, stepId) => activeWorkflowStepAppMetadataByStepId(state).get(stepId);
export const activeWorkflowStepByIdAppAction = (state, stepId) => {
  const step = activeWorkflowStepById(state, stepId);
  return activeWorkflowStepByIdApp(state, stepId)?.actions?.find((a) => a.id === step?.actionId);
};
export const activeWorkflowStepAppByStepId = activeWorkflowStepByIdApp;
export const workflowStepAppById = activeWorkflowStepByIdApp;

export const workflowComments = (state) => state.workflow.workflowComments;
export const activeWorkflowStepComments = createSelector(
  'activeWorkflowStepComments',
  [workflowComments, (state) => selectors.param(state, 'workflowId'), (_, stepId) => stepId], //
  (workflowComments, workflowId, stepId) => workflowComments?.[workflowId]?.filter((n) => n.stepId === stepId) || []
);

export const stepFunctionById = createSelector(
  'stepFunctionById',
  workflows,
  (_, functionId) => functionId,
  (workflows, functionId) => workflows?.find((n) => n?.type === 'function' && n?.id === functionId)
);

export const activeWorkflowLogs = createSelector(
  'activeWorkflowLogs',
  [(state) => selectors.param(state, 'workflowId'), (state) => selectors.query(state, 'workflowId'), workflowLogs], //
  (workflowId, queryWorkflowId, logs) => logs?.filter((n) => (workflowId ? n?.workflowId === workflowId : n?.workflowId === queryWorkflowId))?.sort(sortByProp('startedOn', 'desc'))
);

export const activeWorkflowIsLocked = createSelector(
  'activeWorkflowIsLocked',
  [(state) => selectors.param(state, 'workflowVersion')], //
  (workflowVersion) => workflowVersion !== 'draft'
);
export const activeWorkflowIsEditable = createSelector(
  'activeWorkflowIsEditable',
  [activeWorkflowIsLocked, (state) => selectors.permission(state, 'workflow:edit')], //
  (isLocked, canEdit) => !isLocked && canEdit
);
export const activeWorkflowIsPublishedAndEditable = createSelector(
  'activeWorkflowIsPublishedAndEditable',
  [(state) => selectors.param(state, 'workflowVersion'), (state) => selectors.permission(state, 'workflow:edit')], //
  (workflowVersion, canEdit) => workflowVersion !== 'draft' && canEdit
);

export const activeInputState = (state) => state.workflow.activeInputState;
export const activeInputStateById = createSelector(
  'activeInputStateById',
  activeInputState,
  (_, inputId) => inputId,
  (activeInputState, inputId) => (activeInputState?.inputId === inputId ? activeInputState : EMPTY_OBJECT)
);
export const activeInputStateByIdStepId = createSelector('activeInputStateByIdStepId', [activeInputStateById], (activeInputState) => activeInputState?.stepId);
export const activeInputStateByIdIndex = createSelector('activeInputStateByIdIndex', [activeInputStateById], (activeInputState) => activeInputState?.index);
export const activeInputStateByIdInputId = createSelector('activeInputStateByIdInputId', [activeInputStateById], (activeInputState) => activeInputState?.inputId);

export const activeInputArgument = createSelector('activeInputArgument', activeInputState, activeWorkflowStepAppAction, (activeInputState, action) => action?.arguments?.find((n) => n?.key === activeInputState?.inputArgKey));
export const activeInputArgumentValue = createSelector('activeInputArgumentValue', activeInputState, activeWorkflowStep, (activeInputState, step) => step?.arguments?.[activeInputState?.inputArgKey]);

export const workflowRunUpdate = (state) => state.workflow.workflowRunUpdate;
export const workflowIsRunning = createSelector('workflowIsRunning', workflowRunUpdate, (workflowRunUpdate) => workflowRunUpdate?.isRunning === true);
export const workflowIsRunningSlice = createSelector('workflowIsRunningSlice', workflowRunUpdate, (workflowRunUpdate) => workflowRunUpdate?.isRunningSlice === true);
export const workflowIsCancelling = createSelector('workflowIsCancelling', workflowRunUpdate, (workflowRunUpdate) => workflowRunUpdate?.isCancelling === true && workflowRunUpdate?.isRunning === true);
export const workflowLiveOperationLogs = createSelector('workflowLiveOperationLogs', workflowRunUpdate, (workflowRunUpdate) => workflowRunUpdate?.operationLogs);
export const workflowStepRunCount = createSelector('workflowStepRunCount', workflowRunUpdate, (workflowRunUpdate) => workflowRunUpdate?.stepRunCount);
export const activeWorkflowSnapshots = createSelector(
  'activeWorkflowSnapshots',
  [(state) => selectors.param(state, 'workflowId'), (state) => selectors.query(state, 'workflowId'), workflowSnapshots], //
  (workflowId, queryWorkflowId, snapshots) => snapshots?.filter((n) => (workflowId ? n?.workflowId === workflowId : n?.workflowId === queryWorkflowId))?.sort(sortByProp('createdOn', 'desc'))
);
export const activeWorkflowSnapshotVersionById = createSelector(
  'activeWorkflowSnapshotVersionById',
  [activeWorkflowSnapshots, (_, snapshotId) => snapshotId], //
  (snapshots, snapshotId) => snapshots?.find((n) => n?.id === snapshotId)?.version
);

export const workflowSnapshotDiffs = createSelector(
  'workflowSnapshotDiffs',
  [activeWorkflowSnapshots, workflows, (state) => selectors.param(state, 'workflowId')], //
  (snapshots, workflows, workflowId) => {
    const draftWorkflow = workflows?.find((n) => n?.id === workflowId && n?.version === 'draft');
    const lastPublishedSnapshot = snapshots?.filter((n) => n?.version !== 'draft')?.[0];
    return buildWorkflowSnapshotDiffs(lastPublishedSnapshot?.workflow, draftWorkflow);
  }
);

export const recentlyUsedApps = (state) => state.workflow.recentlyUsedApps;

export const activeWorkflowCredentials = createSelector('activeWorkflowCredentials', selectors.credentials, activeWorkflow, companyId, (credentials, workflows, companyId) => {
  const stepsWithCredentials = workflows?.steps?.filter((n) => !!n?.credentialId);
  const relevantCredentials = stepsWithCredentials?.map((n) => credentials?.find((c) => c?.credentialId === n?.credentialId));
  return relevantCredentials?.filter((n) => n?.companyId === companyId);
});

export const firstActionShouldHighlight = createSelector('firstActionShouldHighlight', activeWorkflow, (activeWorkflow) => {
  const steps = activeWorkflow?.steps;
  return steps?.length === 1 && steps[0]?.type === 'trigger' && steps[0]?.appSlug !== 'triggerSelector';
});

export const activeWorkflowCredentialColors = createSelector('activeWorkflowCredentialColors', activeWorkflowCredentials, companyId, (credentials, companyId) => {
  const credentialUsage = {};
  credentials
    ?.filter((cred) => cred.companyId === companyId)
    ?.forEach((cred) => {
      const key = `${cred.appId}-${cred.credentialId}`;
      if (!credentialUsage[key]) {
        credentialUsage[key] = {
          appId: cred.appId,
          credentialId: cred.credentialId,
          count: 0
        };
      }
      credentialUsage[key].count++;
    });

  const credentialsByApp = {};
  Object.values(credentialUsage).forEach(({ appId, credentialId }) => {
    if (!credentialsByApp[appId]) {
      credentialsByApp[appId] = new Set();
    }
    credentialsByApp[appId].add(credentialId);
  });

  const appsWithMultipleCredentials = Object.entries(credentialsByApp)
    .filter(([_, credIds]) => credIds.size > 1)
    .map(([appId]) => appId);

  const colorMap = {};
  Object.values(credentialUsage)
    .filter(({ appId }) => appsWithMultipleCredentials.includes(appId))
    .sort((a, b) => a.credentialId.localeCompare(b.credentialId))
    .forEach(({ credentialId }) => {
      colorMap[credentialId] = computeColor(credentialId, true);
    });

  return colorMap;
});

export const activeWorkflowIntegrationId = createSelector('activeWorkflowIntegrationId', activeWorkflow, (n) => n?.integrationId);
export const activeWorkflowTestInstallationId = createSelector('activeWorkflowTestInstallationId', activeWorkflow, (n) => n?.installationId);
export const activeWorkflowIsEmbedded = createSelector('activeWorkflowIsEmbedded', activeWorkflow, (activeWorkflow) => !!activeWorkflow?.integrationId);
export const embeddedStepHasValidCredentials = createSelector(
  'embeddedStepHasValidCredentials',
  [activeWorkflowIsEmbedded, (state) => selectors.embeddedIntegrationById(state, activeWorkflowIntegrationId(state)), (state) => state.settings.embeddedEndUserId, activeWorkflowStep, activeWorkflowTestInstallationId, (state) => selectors.activeCompany(state)?.settings?.embeddedAppId], //
  (isEmbedded, embeddedIntegration, embeddedEndUserId, step, testInstallationId, companyEmbeddedAppId) => {
    if (!isEmbedded) {
      return true;
    }

    // The embedded app is the company's `settings.embeddedAppId` — the app credential
    // exchange provisions per installation — exactly as the runtime identifies it
    // (`isStepEmbeddedApp`). `integration.appId` is the integration's primary connector
    // app and is judged by its test installation connection like any other connector.
    // Without a test installation (none active for the test end user) the embedded app
    // cannot be provisioned, so the Connect tab must stay visible to offer "Setup Test
    // Credentials" instead of hiding the only surface that explains why the step fails.
    const isEmbeddedApp = !!embeddedIntegration?.usesEmbeddedApp && !!companyEmbeddedAppId && step?.appId === companyEmbeddedAppId;
    if (isEmbeddedApp) {
      return !!testInstallationId;
    }

    if (!embeddedEndUserId) {
      return false;
    }

    const integrationApps = embeddedIntegration?.installations?.[0]?.apps || [];
    if (integrationApps.length === 0) {
      return true;
    }

    return integrationApps.every((app) => app.isConnected === true);
  }
);

export const activeWorkflowUsesEnvironments = createSelector('activeWorkflowUsesEnvironments', activeWorkflow, (activeWorkflow) => {
  const steps = activeWorkflow?.steps || stableArray;
  const anyStepUsesEnvironments = steps //
    ?.filter((step) => step?.type !== 'trigger')
    ?.some((step) => step?.options?.connectionType === 'environment');

  const anyTriggerUsesEnvironments = steps //
    ?.filter((step) => step?.type === 'trigger')
    ?.some((step) => Array.isArray(step?.arguments?.environments) && step?.arguments?.environments?.length > 0);

  return anyStepUsesEnvironments || anyTriggerUsesEnvironments;
});

export const multiSelectedStepIds = (state) => state.workflow?.selectedSteps;
export const workflowStepTabMemory = (state, { stepId, tabKey }) => state.workflow?.stepTabMemory?.[stepId]?.[tabKey];
export const isStepSelected = createSelector('isStepSelected', [multiSelectedStepIds, (_, stepId) => stepId], (selectedSteps, stepId) => !!selectedSteps?.includes(stepId));
export const selectedStepIds = createSelector('selectedStepIds', [(state) => query(state, 'stepId'), multiSelectedStepIds], (stepId, stepIds) => {
  return [...new Set([stepId, ...(Array.isArray(stepIds) ? stepIds : [])])].filter((n) => n !== undefined && n !== WHEN_CALLED_SELECTION);
});

export const activeWorkflowSortFieldOptions = createSelector(
  'activeWorkflowSortFieldOptions',
  [(state) => selectors.query(state, 'stepId'), activeWorkflow, selectors.appsVersions, (_, isTransformAction, __) => isTransformAction, (_, __, isTransformApp) => isTransformApp], //
  (stepId, activeWorkflow, apps, isTransformAction, isTransformApp) => {
    if (isTransformAction || isTransformApp) {
      return getTransformAttributes({ stepId, apps, steps: activeWorkflow?.steps })
        ?.map((n) => ({ label: formatNestedAttributeKey(n.key), value: n.key }))
        ?.filter((n) => !!n.value);
    }
    return null;
  }
);

export const isIterateChild = createSelector(
  'isIterateChild',
  [(state) => selectors.query(state, 'stepId'), activeWorkflow, selectors.appsMetadata], //
  (stepId, activeWorkflow, appsMetadata) => {
    const steps = activeWorkflow?.steps;
    const graph = activeWorkflow?.graph;

    return getParentIterationStepIds(stepId, steps, appsMetadata, graph)?.length > 0;
  }
);

export const memoryFeatureBoxBullets = createSelector(
  'memoryFeatureBoxBullets',
  [selectors.activeWorkflowMemories, (state) => workflowScreenSelectors.activeWorkflowStep(state)?.options?.memory], //
  (workflowMemories, memory) => {
    return memory?.map((n) => {
      const key = n?.key;
      let memoryValue = workflowMemories.find((m) => m.memoryKey === key)?.memoryValue;
      memoryValue = memoryValue?.length > 64 ? `${memoryValue?.slice(0, 64)}...` : memoryValue;
      return `${key} - ${memoryValue || 'Unset'}`;
    });
  }
);

export const iterationParentStep = createSelector(
  'iterationParentStep',
  [activeWorkflow, selectors.appsMetadata, (state) => selectors.query(state, 'stepId')], //
  (activeWorkflow, apps, stepId) => {
    const steps = activeWorkflow?.steps;
    const graph = activeWorkflow?.graph;

    const parentStepId = getParentIterationStepIds(stepId, steps, apps, graph)?.[0];
    return steps?.find((n) => n?.id === parentStepId);
  }
);

export const tutorialScriptBySlug = createSelector(
  'tutorialScriptBySlug',
  tutorialScripts,
  (_, slug) => slug,
  (scripts, slug) => scripts?.[slug]
);

export const canvasStepCornerTag = createSelector(
  'canvasStepCornerTag',
  [
    (state) => query(state, 'stepId'), //
    (state, stepId) => activeWorkflowStepById(state, stepId),
    (state, stepId) => activeWorkflowStepByIdAppAction(state, stepId),
    (state, stepId) => activeWorkflowStepDependantStepsByStepId(state, stepId),
    (state) => activeWorkflowStepDependantStepsByStepId(state, query(state, 'stepId')),
    (state) => activeWorkflowStepById(state, query(state, 'stepId'))
  ],
  (queryStepId, step, appAction, dependantSteps, dependantStepsOfQueryStep, selectedStep) => {
    const operation = appAction?.operation;
    const isTrigger = step?.type === 'trigger';
    const isReadStep = operation === 'read';
    const appSlug = step?.appSlug?.toLowerCase() || '';
    const isMapStep = appSlug === 'map';
    const isTransformStep = appSlug === 'transform';
    const isUsedByOtherSteps = (dependantSteps?.length ?? 0) > 0;
    const isUnused = !isTrigger && (isReadStep || isMapStep || isTransformStep) && !isUsedByOtherSteps;

    const isDependent = !!queryStepId && queryStepId !== step?.id && dependantStepsOfQueryStep?.some((s) => s.id === step?.id);

    const indicator = isUnused ? 'unused' : isDependent ? 'dependent' : null;
    const selectedStepLabel = selectedStep?.message || selectedStep?.stepNumber;
    const title = isUnused ? 'This step reads information but does not use it in any other steps.' : isDependent ? `This step depends on the output of the selected step ${selectedStepLabel}.` : null;

    return { indicator, title };
  }
);

const workflowScreenSelectors = {
  workflowById,
  activeWorkflow,
  activeWorkflowMetadata,
  activeWorkflowEnvironment,
  activeWorkflowMemoryEnvironmentId,
  activeWorkflowSimpleSteps,
  activeWorkflowApps,
  activeWorkflowArguments,
  activeWorkflowArgumentInstances,
  activeWorkflowArgumentEnvironments,
  activeWorkflowAppEnvironmentConnections,
  activeWorkflowStep,
  activeWorkflowStepsById,
  activeWorkflowStepDependencies,
  activeWorkflowDependantStepsByStepId,
  activeWorkflowStepDependantStepsByStepId,
  activeWorkflowStepApp,
  activeWorkflowStepAppMetadata,
  activeWorkflowStepAppAction,
  activeWorkflowStepAppActionArgumentDefinitions,
  stepFunctionById,
  activeWorkflowStepAppAPI,
  activeWorkflowStepAppAPIArgumentDefinitions,
  activeWorkflowStepArguments,
  activeWorkflowStepArgumentDefinitions,
  activeWorkflowStepOptions,
  activeWorkflowStepCredentials,
  activeWorkflowStepAuth,
  activeWorkflowStepActiveCredentials,
  activeWorkflowStepActiveAuth,
  activeWorkflowGraphStepType,
  activeWorkflowStepArgument,
  activeWorkflowStepById,
  activeWorkflowStepAppsByStepId,
  activeWorkflowStepAppMetadataByStepId,
  activeWorkflowStepByIdApp,
  activeWorkflowStepByIdAppMetadata,
  activeWorkflowStepByIdAppAction,
  activeWorkflowStepAppByStepId,
  activeWorkflowLogs,
  activeWorkflowIsLocked,
  activeWorkflowIsEditable,
  activeInputState,
  activeInputStateById,
  activeInputStateByIdStepId,
  activeInputStateByIdIndex,
  activeInputStateByIdInputId,
  activeInputArgument,
  activeInputArgumentValue,
  workflowComments,
  activeWorkflowStepComments,
  workflowRunUpdate,
  workflowIsRunning,
  workflowIsRunningSlice,
  workflowIsCancelling,
  workflowLiveOperationLogs,
  workflowStepRunCount,
  activeWorkflowSnapshots,
  activeWorkflowSnapshotVersionById,
  workflowSnapshotDiffs,
  activeWorkflowStepAppTriggerData,
  workflowTriggerData,
  activeWorkflowStepAppTriggerActions,
  activeWorkflowStepIsTrigger,
  recentlyUsedApps,
  activeWorkflowCredentialColors,
  activeWorkflowIsPublishedAndEditable,
  activeWorkflowStepOptionsArgument,
  activeWorkflowUsesEnvironments,
  activeWorkflowIsEmbedded,
  activeWorkflowIntegrationId,
  activeWorkflowTestInstallationId,
  embeddedStepHasValidCredentials,
  activeWorkflowEnvironmentConnections,
  workflowStepAppById,
  firstActionShouldHighlight,
  selectedStepIds,
  isStepSelected,
  activeWorkflowSortFieldOptions,
  isIterateChild,
  memoryFeatureBoxBullets,
  iterationParentStep,
  tutorialScriptBySlug,
  workflowCanvasStepDepth,
  canvasStepCornerTag
};

export default workflowScreenSelectors;
